import React, { useState, useEffect } from 'react'
import { Col, Table, Button, Spin, message, Row, Tooltip } from 'antd'
import { useLocation, useNavigate } from 'react-router-dom'
import OrderDrawer from '../Orders/OrderDrawer'
import { OrderStatusTag } from '../Orders/OrderStatuses'
import { Link } from 'react-router-dom'
import FormattedDate from '../Utils/FormattedDate'
import { ACCEPT_ORDERS, COMPLETE_ORDERS } from './OrderQueries'
import { useApolloClient, useMutation } from '@apollo/client'
import { useSession } from '@slerp/accounts'
import { PrinterOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { print } from './utils'
import { TableCellNoWrap, TableHeaderTooltip } from 'components/Common'
import styled from '@emotion/styled'

interface OrderStore {
  name: string
  address: {
    zip: string
  }
}

interface Order {
  store_id: string
  transaction_id: string
  key: string
  store: OrderStore
  subtotal: number
  total: number
  fulfillment_type: 'delivery' | 'pickup'
  fulfillment_date: string
  create_device_category: null | string
  create_platform: null | string
  create_os_type: null | string
  status: 'pending' | 'accepted' | 'fulfilled' | 'refunded' | 'rejected'
  payment_status: null | 'captured' | 'partially_refunded' | 'fully_refunded'
  inserted_at: string
}

interface OrdersListProps {
  orders: Order[]
  loading?: boolean
  refetch?: () => {}
  transactionId?: string
}

interface OrderLinkProps {
  text: string
  onClick: () => void
}

const setSearchParams = (location: Location) => {
  return new URLSearchParams(location.search)
}

const humanizePlatform = (str: string) => {
  return str === 'ios' ? 'iOS' : str?.charAt(0).toUpperCase() + str?.slice(1)
}

export const OrderLink = ({ onClick, text }: OrderLinkProps) => (
  <Link to={`#`} onClick={onClick}>
    {text}
  </Link>
)

const OrdersList = (props: OrdersListProps) => {
  const client = useApolloClient()
  const { orders, loading } = props
  const { user } = useSession()
  const location = useLocation() as any
  const navigate = useNavigate()
  const searchParams = setSearchParams(location)
  const transactionId =
    props.transactionId || searchParams.get('transaction_id') || ''

  const columns = [
    {
      title: (
        <TableHeaderTooltip>
          Order ID
          <Tooltip
            title={
              <span>Click the order's ID number to see full details.</span>
            }
          >
            <QuestionCircleOutlined className='_ml-8 _mr-8' />
          </Tooltip>
        </TableHeaderTooltip>
      ),
      dataIndex: 'transaction_id',
      key: 'key',
      render: (text: string, record: Order) => {
        return (
          <div style={{ width: '85px' }} className='transaction-id'>
            <OrderLink
              text={text}
              onClick={() => {
                showDrawer(record)
              }}
            />
          </div>
        )
      }
    },
    {
      title: 'Location',
      dataIndex: ['store', 'name'],
      key: 'store.name',
      render: (storeName: string, order: Order) => {
        return (
          <div className='order-store-info'>
            <p className='name'>{storeName}</p>
            <p className='zip'>{order?.store?.address?.zip}</p>
          </div>
        )
      }
    },
    {
      title: 'Type',
      dataIndex: 'fulfillment_type',
      key: 'fulfillment_type',
      render: (fulfillmentType: 'pickup' | 'delivery') => (
        <TableCellNoWrap className='order-fulfillment-type'>
          {fulfillmentType}
        </TableCellNoWrap>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (status: string, order: Order) => (
        <Cell>{OrderStatusTag(status, order?.payment_status)}</Cell>
      )
    },
    {
      title: <TableCellNoWrap>Subtotal</TableCellNoWrap>,
      dataIndex: 'subtotal',
      key: 'subtotal',
      render: (subtotal: number) => <span>£{subtotal.toFixed(2)}</span>
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      render: (total: number) => <span>£{total.toFixed(2)}</span>
    },
    {
      title: <div className='_text-right'>Ordered At</div>,
      dataIndex: 'inserted_at',
      key: 'inserted_at',
      render: (orderedAt: Date) => {
        return (
          <TableCellNoWrap className='_text-right'>
            <FormattedDate>{orderedAt}</FormattedDate>
          </TableCellNoWrap>
        )
      }
    },
    {
      title: <div className='_text-right'>Fulfilment Date</div>,
      dataIndex: 'fulfillment_date',
      key: 'fulfillment_date',
      render: (fulfillmentDate: Date) => (
        <div className='_text-right'>
          {fulfillmentDate ? (
            <FormattedDate>{fulfillmentDate}</FormattedDate>
          ) : (
            'ASAP'
          )}
        </div>
      )
    },
    {
      title: 'Platform',
      dataIndex: 'platform',
      key: 'platform',
      render: (platform: string, order: Order) => (
        <span className='order-platform'>
          {humanizePlatform(order?.create_platform || '')} -{' '}
          {humanizePlatform(order?.create_device_category || '')}
        </span>
      )
    },
    {
      align: 'right',
      title: <TableCellNoWrap>Print</TableCellNoWrap>,
      dataIndex: 'transaction_id',
      key: 'print',
      render: (orderId: string) => (
        <Button
          size='small'
          icon={<PrinterOutlined />}
          onClick={() => print(orderId)}
        />
      )
    }
  ]

  const [activeOrder, setActiveOrder] = useState<Order | null>(null)
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [selectedPendingOrders, setSelectedPendingOrders] = useState<
    Array<string>
  >([])
  const [selectedAcceptedOrders, setSelectedAcceptedOrders] = useState<
    Array<string>
  >([])
  const showDrawer = (record: Order) => {
    setDrawerVisible(true)
    setActiveOrder(record)
    if (location.pathname.startsWith('/orders')) {
      navigate(`/orders/${record.transaction_id}`)
    }
    return true
  }

  const onDrawerClose = () => {
    setDrawerVisible(false)
    setActiveOrder(null)
    if (location.pathname.startsWith('/orders/')) {
      navigate(`/orders`)
    }
  }

  const onSelectChange = (
    transactionIds: Array<string>,
    orders: Array<Order>
  ) => {
    const pendingOrders = orders.filter(
      (order: Order) => order.status === 'pending'
    )
    const acceptedOrders = orders.filter(
      (order: Order) => order.status === 'accepted'
    )
    setSelectedPendingOrders(
      pendingOrders.map((pendingOrder: Order) => pendingOrder.transaction_id)
    )
    setSelectedAcceptedOrders(
      acceptedOrders.map((acceptedOrder: Order) => acceptedOrder.transaction_id)
    )
  }

  const rowSelection = {
    selectedRowKeys: [...selectedPendingOrders, ...selectedAcceptedOrders],
    onChange: onSelectChange,
    getCheckboxProps: ({ status }: { status: string }) => ({
      disabled: !['pending', 'accepted'].includes(status)
    })
  }

  // Reset selection when we change the date
  useEffect(() => {
    setSelectedPendingOrders([])
    setSelectedAcceptedOrders([])
  }, [orders && orders.length])

  useEffect(() => {
    if (transactionId && orders) {
      const order = orders.find((ord) => ord.transaction_id === transactionId)
      if (order) {
        showDrawer(order)
      } else {
        setDrawerVisible(false)
        setActiveOrder(null)
      }
    }
  }, [transactionId, orders])

  const [acceptOrders, { loading: acceptLoading, error: acceptError }] =
    useMutation(ACCEPT_ORDERS, {
      fetchPolicy: 'no-cache'
    })

  const [
    completeOrders,
    { loading: completingOrdersLoading, error: completingOrdersError }
  ] = useMutation(COMPLETE_ORDERS, {
    fetchPolicy: 'no-cache'
  })

  const acceptOrdersHandler = (e: React.MouseEvent) => {
    if (selectedPendingOrders.length > 0) {
      message.loading('Updating...')
      acceptOrders({
        variables: { transactionIds: selectedPendingOrders, userId: user?.id }
      })
        .then((result) => {
          setSelectedPendingOrders([])
          client.resetStore()
        })
        .catch((error: Error) => {
          message.destroy()
          message.error(
            `Bulk accept orders encountered an error ${error.message}`,
            3
          )
        })
    }
  }

  const setOrderToCompleteHandler = (e: React.MouseEvent) => {
    if (selectedAcceptedOrders.length > 0) {
      message.loading('Updating...')

      completeOrders({
        variables: { transactionIds: selectedAcceptedOrders, userId: user?.id }
      })
        .then((result) => {
          setSelectedAcceptedOrders([])
          client.resetStore()
        })
        .catch((error: Error) => {
          message.destroy()
          message.error(
            `Bulk fulfill orders encountered an error ${error.message}`,
            3
          )
        })
    }
  }

  const ordersInfo = () => (
    <>
      {selectedPendingOrders.length > 0 && (
        <Row gutter={[8, 8]} align='middle'>
          <Col>
            Total pending orders selected: {selectedPendingOrders.length}
          </Col>
          <Col>
            <Button
              type='primary'
              size='small'
              onClick={acceptOrdersHandler}
              data-testid='order-list-bulk-accept-order-btn'
            >
              Accept
            </Button>
          </Col>
          {acceptLoading ? (
            <Col>
              Updating... <Spin />
            </Col>
          ) : (
            <></>
          )}
          {acceptError ? (
            <Col> - Error updating... Please try again later.</Col>
          ) : (
            <></>
          )}
        </Row>
      )}
      {selectedAcceptedOrders.length > 0 && (
        <Row gutter={[8, 8]} align='middle'>
          <Col>
            Total accepted orders selected: {selectedAcceptedOrders.length}
          </Col>
          <Col>
            <Button
              type='primary'
              size='small'
              data-testid='order-list-bulk-fulfill-order-btn'
              onClick={setOrderToCompleteHandler}
            >
              Fulfill
            </Button>
          </Col>
          {completingOrdersLoading ? (
            <Col>
              Updating... <Spin />
            </Col>
          ) : (
            <></>
          )}
          {completingOrdersError ? (
            <Col> - Error updating... Please try again later.</Col>
          ) : (
            <></>
          )}
        </Row>
      )}
    </>
  )

  return (
    <>
      <div style={{ marginBottom: 15 }}>{ordersInfo()}</div>
      <Table
        data-testid='orders-list-table'
        rowSelection={rowSelection as any}
        dataSource={orders}
        columns={columns as any}
        pagination={{
          defaultPageSize: 100,
          position: ['bottomCenter'],
          showSizeChanger: true
        }}
        loading={loading}
        footer={ordersInfo}
      />

      {activeOrder && (
        <OrderDrawer
          transactionId={activeOrder.transaction_id}
          visible={drawerVisible}
          onClose={onDrawerClose}
          ordersRefetch={props.refetch}
        />
      )}
    </>
  )
}

const Cell = styled.div`
  display: flex;
  justify-content: center;
  justify-items: center;
`

export default OrdersList
